/*
 * WSS product layout (2026-10-15)
 *
 * Progressive enhancement for snippets/product.liquid when the static-product
 * section setting "Use WSS product layout" (wss_layout) is on. Loaded with
 * defer from sections/static-product.liquid, only when that setting is on.
 *
 * 1. Product description: the full description HTML is rendered by Liquid in
 *    [data-wss-desc-raw]; this script splits it into accordion sections:
 *      - theme tab markup (ul.tabs + ul.tabs-content) becomes one section per tab
 *      - h2 to h6 headings, and paragraphs that are only bold text ending in ":"
 *        (or a short heading word like "Details"), start new sections
 *      - content before the first heading becomes "Overview"
 *      - paragraphs starting with "Note:" are collected into a highlighted
 *        notes section that is open by default
 *    If anything throws, the raw description stays visible (nothing is lost).
 * 2. Sticky columns (>= 990px): computes the sticky top offset for the gallery
 *    column and the buy column so a column taller than the window sticks by its
 *    bottom edge instead of hiding content.
 * 3. Phone gallery dots (< 720px, when Empire's swipe gallery is active).
 * 4. Phone sticky buy bar (< 750px): appears after the main Add to cart button
 *    scrolls out of view; its button clicks the theme's real Add to cart button.
 * 5. Support video: replaces the lazyframe box (blank until clicked, since the
 *    theme never loads lazyframe's stylesheet) with thumbnail, play button,
 *    loading spinner and a message when the video is missing or slow.
 *
 * No cart logic lives here. Nothing in this file runs in quick shop or the
 * featured product section (those never render [data-wss-pdp]).
 */
(function () {
  'use strict';

  var slice = Array.prototype.slice;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function toArray(list) {
    return list ? slice.call(list) : [];
  }

  function norm(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  var MEDIA_SELECTOR = 'img,iframe,video,audio,table,svg,object,embed,picture,canvas,input,select,textarea,button';
  var INLINE_TAGS = /^(A|ABBR|B|BDI|BDO|BR|CITE|CODE|DATA|DFN|EM|FONT|I|IMG|KBD|LABEL|MARK|Q|S|SAMP|SMALL|SPAN|STRIKE|STRONG|SUB|SUP|TIME|U|VAR|WBR)$/;
  var INVISIBLE_TAGS = /^(META|LINK|STYLE|SCRIPT|TEMPLATE|NOSCRIPT)$/;
  var BLOCK_IN_LI = /^(P|UL|OL|TABLE|DIV|H[1-6]|BLOCKQUOTE|SECTION|ARTICLE|FIGURE|DL|PRE|IFRAME)$/;

  /* True when a node carries something a shopper can see. */
  function hasContent(node) {
    if (!node) return false;
    if (node.nodeType === 3) return /\S/.test(node.nodeValue);
    if (node.nodeType !== 1) return false;
    if (INVISIBLE_TAGS.test(node.tagName)) return false;
    if (node.matches(MEDIA_SELECTOR)) return true;
    if (node.querySelector(MEDIA_SELECTOR)) return true;
    return /\S/.test(node.textContent);
  }

  function isTag(node, re) {
    return !!node && node.nodeType === 1 && re.test(node.tagName);
  }

  function hasClass(node, name) {
    return !!node && node.nodeType === 1 && node.classList.contains(name);
  }

  function childElements(node, tagName) {
    return toArray(node.children).filter(function (child) {
      return !tagName || child.tagName === tagName;
    });
  }

  /*
   * Put loose inline content (text, links, bold, line breaks) that sits
   * directly in the description into paragraphs, so every node we sort into
   * sections is a block. Drops comments and whitespace-only text.
   */
  function blockify(nodes, doc) {
    var out = [];
    var run = null;

    function flush() {
      if (run && toArray(run.childNodes).some(hasContent)) out.push(run);
      run = null;
    }

    nodes.forEach(function (node) {
      if (node.nodeType === 8) return; /* comment, includes <!-- more --> */
      if (node.nodeType === 3 || (node.nodeType === 1 && INLINE_TAGS.test(node.tagName))) {
        if (!run) {
          if (node.nodeType === 3 && !/\S/.test(node.nodeValue)) return;
          run = doc.createElement('p');
        }
        run.appendChild(node);
        return;
      }
      if (node.nodeType !== 1) return;
      flush();
      out.push(node);
    });
    flush();
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* Heading detection                                                   */
  /* ------------------------------------------------------------------ */

  var HEADING_WORDS = /^(overview|description|product description|details|product details|features|key features|product features|features and benefits|specifications|specs|product specifications|technical specifications|highlights|product highlights|applications|benefits|warranty|installation|installation notes|installation instructions|instructions|includes|kit includes|kit contents|kit components|what's included|what is included|items included|in the box|maintenance|maintenance tips|compatibility|dimensions)$/i;

  function cleanTitle(text) {
    return norm(text).replace(/\s*:+\s*$/, '');
  }

  /* Text that reads like a section title rather than a sentence or notice. */
  function isTitleText(text) {
    var t = norm(text);
    var bare = t.replace(/:+$/, '').trim();
    if (bare.length < 2 || bare.length > 70) return false;
    if (/[.!;,]$/.test(bare)) return false;
    if (/^[*#\-]/.test(bare)) return false;
    if (/https?:|www\.|@/i.test(bare)) return false;
    if (/^(please\s+)?note\b/i.test(bare)) return false;
    if (bare.split(' ').length > 10) return false;
    /* SHOUTED notices ("SHIPPING IS NOT FREE ON THIS PRODUCT") stay in the body */
    if (bare.length > 12 && bare === bare.toUpperCase() && /[A-Z]/.test(bare)) return false;
    return true;
  }

  function isBoldEl(node) {
    return isTag(node, /^(STRONG|B)$/);
  }

  /*
   * If a paragraph's visible content is only bold text (optionally wrapped
   * in span/em/u, with an optional ":" after the bold), return that text.
   */
  function boldOnlyText(p) {
    var found = false;

    function check(node, insideBold) {
      var kids = toArray(node.childNodes);
      for (var i = 0; i < kids.length; i += 1) {
        var kid = kids[i];
        if (kid.nodeType === 8) continue;
        if (kid.nodeType === 3) {
          if (!/\S/.test(kid.nodeValue)) continue;
          if (insideBold) { found = true; continue; }
          if (/^\s*:\s*$/.test(kid.nodeValue)) continue;
          return false;
        }
        if (kid.nodeType !== 1) continue;
        if (kid.tagName === 'BR' || INVISIBLE_TAGS.test(kid.tagName)) continue;
        if (isBoldEl(kid)) {
          if (!check(kid, true)) return false;
          continue;
        }
        if (/^(SPAN|EM|I|U|FONT)$/.test(kid.tagName)) {
          if (!check(kid, insideBold)) return false;
          continue;
        }
        return false;
      }
      return true;
    }

    if (!check(p, false) || !found) return null;
    return norm(p.textContent);
  }

  /*
   * "<p><strong>Installation Notes:</strong><br>Text...</p>": a bold label
   * ending in ":" followed by a line break. Returns the label element and
   * the nodes to remove so the rest of the paragraph becomes body text.
   * A label followed by text on the same line ("Voltage: 115V") is not a
   * heading and returns null.
   */
  function leadingBoldLabel(p) {
    var kids = toArray(p.childNodes);
    var i = 0;
    while (i < kids.length && kids[i].nodeType === 3 && !/\S/.test(kids[i].nodeValue)) i += 1;
    var label = kids[i];
    if (!isBoldEl(label)) return null;
    var text = norm(label.textContent);
    if (!/:$/.test(text)) return null;
    var j = i + 1;
    while (j < kids.length && kids[j].nodeType === 3 && !/\S/.test(kids[j].nodeValue)) j += 1;
    var br = kids[j];
    if (!isTag(br, /^BR$/)) {
      /* the break may sit inside the bold element: <strong>Label:<br></strong> */
      var innerBr = label.lastElementChild;
      if (!isTag(innerBr, /^BR$/)) return null;
      br = innerBr;
    }
    var rest = kids.slice(j + 1);
    if (!rest.some(hasContent)) return null;
    return { title: text, remove: kids.slice(i, j + 1).concat(br === kids[j] ? [] : [br]) };
  }

  /*
   * Split a run of block nodes on headings. Nothing is changed in the DOM
   * until the split is final (see applySplit).
   */
  function splitOnHeadings(nodes, allowBold) {
    var lead = [];
    var sections = [];
    var current = lead;
    var removals = [];

    function start(title, origin, headingNode) {
      var section = { title: cleanTitle(title), nodes: [], origin: origin, headingNode: headingNode };
      sections.push(section);
      current = section.nodes;
    }

    nodes.forEach(function (node) {
      if (node.nodeType === 1) {
        if (isTag(node, /^H[1-6]$/)) {
          if (!hasContent(node)) return; /* empty spacer heading */
          var headingText = norm(node.textContent);
          if (node.tagName !== 'H1' && isTitleText(headingText)) {
            start(headingText, 'heading', node);
            return;
          }
        } else if (allowBold && isTag(node, /^P$/)) {
          var bold = boldOnlyText(node);
          if (bold && isTitleText(bold) && (/:$/.test(bold) || HEADING_WORDS.test(cleanTitle(bold)))) {
            start(bold, 'bold', node);
            return;
          }
          var label = leadingBoldLabel(node);
          if (label && isTitleText(label.title)) {
            removals = removals.concat(label.remove);
            start(label.title, 'bold');
            current.push(node);
            return;
          }
        }
      }
      current.push(node);
    });

    /*
     * Guard: a run of bold "Label:" lines each followed by one short value
     * is a spec list, not sections. Split on real headings only in that case.
     */
    var boldSections = sections.filter(function (s) { return s.origin === 'bold'; });
    if (allowBold && boldSections.length >= 4) {
      var shortOnes = boldSections.filter(function (s) {
        return norm(s.nodes.map(function (n) { return n.textContent; }).join(' ')).length < 60;
      });
      if (shortOnes.length >= boldSections.length * 0.75) return splitOnHeadings(nodes, false);
    }

    removals.forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    return { lead: lead, sections: sections };
  }

  /* ------------------------------------------------------------------ */
  /* Description to sections                                             */
  /* ------------------------------------------------------------------ */

  function liHoldsBlocks(li) {
    return toArray(li.children).some(function (child) {
      return BLOCK_IN_LI.test(child.tagName);
    });
  }

  /*
   * Returns [{ title, nodes, kind }] where kind is 'overview', 'tab',
   * 'heading' or 'note'. Nodes are moved out of `raw` by the caller.
   */
  function buildSections(raw, doc) {
    var top = toArray(raw.childNodes);

    /* Unwrap single wrapper elements around the whole description */
    for (var guard = 0; guard < 5; guard += 1) {
      var meaningful = top.filter(hasContent);
      if (meaningful.length === 1 && isTag(meaningful[0], /^(DIV|SECTION|ARTICLE)$/) && !hasClass(meaningful[0], 'tabs')) {
        top = toArray(meaningful[0].childNodes);
      } else {
        break;
      }
    }

    /* Pass 1: runs of plain content and theme tabs */
    var runs = [];
    var flat = null;
    var i;

    function pushFlat(node) {
      if (!flat) {
        flat = { type: 'flat', nodes: [] };
        runs.push(flat);
      }
      flat.nodes.push(node);
    }

    function pushChildren(node) {
      toArray(node.childNodes).forEach(pushFlat);
    }

    for (i = 0; i < top.length; i += 1) {
      var node = top[i];

      if (isTag(node, /^UL$/) && hasClass(node, 'tabs')) {
        var labels = childElements(node, 'LI');

        if (labels.some(liHoldsBlocks)) {
          /* Tab list used as a plain wrapper around the whole description */
          toArray(node.childNodes).forEach(function (child) {
            if (isTag(child, /^LI$/)) pushChildren(child);
            else pushFlat(child);
          });
          continue;
        }

        /* Label list: find its ul.tabs-content, skipping empty nodes */
        var j = i + 1;
        while (j < top.length && !hasContent(top[j]) && !hasClass(top[j], 'tabs-content')) j += 1;
        var content = (isTag(top[j], /^UL$/) && hasClass(top[j], 'tabs-content')) ? top[j] : null;

        if (content) {
          var panes = childElements(content, 'LI');
          if (panes.length === labels.length) {
            var stray = [];
            panes.forEach(function (pane, index) {
              runs.push({ type: 'tab', title: norm(labels[index].textContent), nodes: toArray(pane.childNodes) });
            });
            flat = null;
            toArray(content.childNodes).forEach(function (child) {
              if (!isTag(child, /^LI$/)) stray.push(child);
            });
            stray.forEach(pushFlat);
          } else {
            /*
             * Labels and panes do not match up, so the theme never showed them
             * as tabs either. Keep every pane's content in order; the labels
             * are placeholders and are dropped.
             */
            toArray(content.childNodes).forEach(function (child) {
              if (isTag(child, /^LI$/)) pushChildren(child);
              else pushFlat(child);
            });
          }
          i = j;
        }
        /* Labels with no content list at all are placeholders: dropped */
        continue;
      }

      pushFlat(node);
    }

    /* Pass 2: split plain runs on headings, assemble in document order */
    var sections = [];
    var trailing = [];

    runs.forEach(function (run) {
      var nodes = blockify(run.nodes, doc);
      if (run.type === 'tab') {
        sections.push({ title: run.title, nodes: nodes, kind: 'tab' });
        return;
      }
      var split = splitOnHeadings(nodes, true);
      if (split.lead.some(hasContent)) {
        if (sections.length === 0) {
          sections.push({ title: 'Overview', nodes: split.lead, kind: 'overview' });
        } else {
          trailing = trailing.concat(split.lead);
        }
      }
      split.sections.forEach(function (s) {
        sections.push({ title: s.title, nodes: s.nodes, kind: 'heading', headingNode: s.headingNode });
      });
    });

    /* Content after the tabs with no heading of its own joins the first section */
    if (trailing.length) {
      if (sections.length) sections[0].nodes = sections[0].nodes.concat(trailing);
      else sections.push({ title: 'Overview', nodes: trailing, kind: 'overview' });
    }

    /* Pass 3: pull "Note:" paragraphs into the notes section */
    var notes = [];
    sections.forEach(function (section) {
      section.nodes = section.nodes.filter(function (n) {
        if (isTag(n, /^(P|DIV)$/) && /^note\s*:/i.test(norm(n.textContent))) {
          notes.push(n);
          return false;
        }
        return true;
      });
    });

    /* Pass 4: drop empty spacer blocks; drop sections left empty; merge repeats */
    var invisible = [];
    var cleaned = [];
    var carry = [];
    sections.forEach(function (section) {
      var kept = [];
      section.nodes.forEach(function (n) {
        if (hasContent(n)) kept.push(n);
        else if (n.nodeType === 1 && n.tagName === 'STYLE') invisible.push(n);
        else if (n.nodeType === 1 && n.querySelector && n.querySelector('style')) invisible.push(n);
      });
      if (!kept.length) {
        /* A heading with nothing under it is still text: show it in the next section */
        if (section.headingNode) carry.push(section.headingNode);
        return;
      }
      if (carry.length) {
        kept = carry.concat(kept);
        carry = [];
      }
      var previous = cleaned[cleaned.length - 1];
      /* Same title twice in a row, or a tab with no label: one section */
      if (previous && (!section.title || previous.title.toLowerCase() === section.title.toLowerCase())) {
        previous.nodes = previous.nodes.concat(kept);
        return;
      }
      cleaned.push({ title: section.title || 'Overview', nodes: kept, kind: section.kind });
    });
    if (carry.length) {
      if (cleaned.length) cleaned[cleaned.length - 1].nodes = cleaned[cleaned.length - 1].nodes.concat(carry);
      else cleaned.push({ title: 'Overview', nodes: carry, kind: 'overview' });
    }

    if (notes.length) {
      var noteText = notes.map(function (n) { return n.textContent; }).join(' ');
      var sizing = /\b(dimensions?|diameters?|size|sizes|sizing|height|heights|measurements?|measure)\b/i.test(noteText);
      cleaned.push({ title: sizing ? 'Sizing notes' : 'Notes', nodes: notes, kind: 'note' });
    }

    return { sections: cleaned, invisible: invisible };
  }

  /* Remove the leading "Note:" from a note paragraph (the section title says it). */
  function stripNotePrefix(p, doc) {
    var walker = doc.createTreeWalker(p, 4 /* NodeFilter.SHOW_TEXT */, null);
    var text = walker.nextNode();
    while (text && !/\S/.test(text.nodeValue)) text = walker.nextNode();
    if (!text) return;
    if (/^\s*note\s*:\s*/i.test(text.nodeValue)) {
      text.nodeValue = text.nodeValue.replace(/^\s*note\s*:\s*/i, '');
      /* Capitalise the first letter left behind */
      text.nodeValue = text.nodeValue.replace(/^([a-z])/, function (m) { return m.toUpperCase(); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Accordion markup                                                    */
  /* ------------------------------------------------------------------ */

  var CHEVRON = '<svg class="wss-acc__chev" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
  var NOTE_ICON = '<svg class="wss-acc__icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.5l9.5 16.5h-19z"/><path d="M12 10v4.5"/><path d="M12 17.2v.3"/></svg>';

  function setOpen(item, open) {
    var trigger = item.querySelector('.wss-acc__trigger');
    var panel = item.querySelector('.wss-acc__panel');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.classList.toggle('is-open', open);
    if (open) {
      panel.removeAttribute('hidden');
    } else if ('onbeforematch' in panel) {
      /* Lets the browser's find-in-page reach collapsed text and open it */
      panel.setAttribute('hidden', 'until-found');
    } else {
      panel.setAttribute('hidden', '');
    }
  }

  function renderAccordion(container, result, uid, doc) {
    var list = doc.createElement('div');
    list.className = 'wss-acc';
    var useRegions = result.sections.length <= 6;

    result.sections.forEach(function (section, index) {
      var id = 'WssAcc-' + uid + '-' + (index + 1);
      var item = doc.createElement('div');
      item.className = 'wss-acc__item' + (section.kind === 'note' ? ' wss-acc__item--note' : '');

      var heading = doc.createElement('h3');
      heading.className = 'wss-acc__heading';

      var trigger = doc.createElement('button');
      trigger.type = 'button';
      trigger.className = 'wss-acc__trigger';
      trigger.id = id + '-button';
      trigger.setAttribute('aria-controls', id + '-panel');

      var title = doc.createElement('span');
      title.className = 'wss-acc__title';
      title.textContent = section.title;

      if (section.kind === 'note') trigger.insertAdjacentHTML('beforeend', NOTE_ICON);
      trigger.appendChild(title);
      if (section.kind === 'note') {
        var pill = doc.createElement('span');
        pill.className = 'wss-acc__pill';
        pill.textContent = 'Read before ordering';
        trigger.appendChild(pill);
      }
      trigger.insertAdjacentHTML('beforeend', CHEVRON);
      heading.appendChild(trigger);

      var panel = doc.createElement('div');
      panel.className = 'wss-acc__panel';
      panel.id = id + '-panel';
      if (useRegions) {
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', trigger.id);
      }

      var body = doc.createElement('div');
      body.className = 'wss-acc__body rte';
      section.nodes.forEach(function (n) {
        if (section.kind === 'note') {
          stripNotePrefix(n, doc);
          n.classList.add('wss-note');
        }
        body.appendChild(n);
      });

      panel.appendChild(body);
      item.appendChild(heading);
      item.appendChild(panel);
      list.appendChild(item);

      setOpen(item, index === 0 || section.kind === 'note');

      trigger.addEventListener('click', function () {
        setOpen(item, trigger.getAttribute('aria-expanded') !== 'true');
      });
      panel.addEventListener('beforematch', function () {
        setOpen(item, true);
      });
    });

    container.appendChild(list);
    result.invisible.forEach(function (n) { container.appendChild(n); });
  }

  /* Empire opens off-site description links in a new tab; keep that */
  function externalLinks(scope) {
    toArray(scope.querySelectorAll('a[href^="http"]')).forEach(function (a) {
      if (a.hostname && a.hostname !== window.location.hostname) {
        a.setAttribute('target', '_blank');
        var rel = a.getAttribute('rel') || '';
        if (!/noopener/.test(rel)) a.setAttribute('rel', norm(rel + ' noopener'));
      }
    });
  }

  function initDescription(pdp) {
    var wrap = pdp.querySelector('[data-wss-desc]');
    if (!wrap) return;
    var raw = wrap.querySelector('[data-wss-desc-raw]');
    if (!raw) return;
    var doc = wrap.ownerDocument;

    try {
      var result = buildSections(raw, doc);
      if (!result.sections.length) {
        wrap.hidden = true; /* the description has no visible content */
        wrap.classList.remove('wss-desc--pending');
        return;
      }
      var mount = doc.createElement('div');
      mount.className = 'wss-desc__sections';
      renderAccordion(mount, result, wrap.getAttribute('data-wss-uid') || 'main', doc);
      raw.parentNode.replaceChild(mount, raw);
      externalLinks(mount);
    } catch (error) {
      /* Leave the raw description in place: every word stays readable */
      if (window.console) console.warn('WSS product description split skipped', error);
    }
    wrap.classList.remove('wss-desc--pending');
  }

  /* ------------------------------------------------------------------ */
  /* Empty product blocks (hidden so they add no gaps or divider lines)  */
  /* ------------------------------------------------------------------ */

  var ROW_BLOCK = /\bproduct-block--(info-tab|sku|collapsible-tab)\b/;

  function markEmptyBlocks(pdp) {
    var previous = null;
    toArray(pdp.querySelectorAll('.product-details > .product-block')).forEach(function (block) {
      var visible = toArray(block.children).some(function (child) {
        return !INVISIBLE_TAGS.test(child.tagName) && !child.classList.contains('product-sku--empty');
      });
      block.classList.toggle('wss-empty', !visible);
      if (!visible) return;
      /* Info rows share 2px dividers: a line above every row that follows another
         visible row, so the last row of the group has no line under it */
      var isRow = ROW_BLOCK.test(block.className);
      block.classList.toggle('wss-row--follows', isRow && !!previous && ROW_BLOCK.test(previous.className));
      block.classList.toggle('wss-row--after-box', isRow && !!previous && /\bproduct-block--key_details\b/.test(previous.className));
      previous = block;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky columns (desktop)                                            */
  /* ------------------------------------------------------------------ */

  function media(query) {
    return window.matchMedia ? window.matchMedia(query) : { matches: false };
  }

  var desktopQuery = media('(min-width: 990px)');
  var phoneQuery = media('(max-width: 749px)');

  function stickyHeaderHeight() {
    var height = 0;
    toArray(document.querySelectorAll('[data-site-header-main], .site-header-sticky, [data-sticky-header]')).forEach(function (el) {
      var position = window.getComputedStyle(el).position;
      if (position === 'fixed' || position === 'sticky') {
        height = Math.max(height, el.getBoundingClientRect().height);
      }
    });
    /* The WSS header pins its search row once the page scrolls past it (sections/wss-header.liquid),
       so reserve that height even before it is pinned. */
    if (document.querySelector('[data-wssh-sticky]')) {
      var pinned = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--wssh-stuck-h'));
      if (pinned > 0) height = Math.max(height, pinned);
    }
    return height;
  }

  function initStickyColumns(pdp) {
    var left = pdp.querySelector('[data-wss-left]');
    var right = pdp.querySelector('.product-main');
    if (!left || !right) return;
    var frame = null;

    function update() {
      frame = null;
      if (!desktopQuery.matches) {
        pdp.style.removeProperty('--wss-left-top');
        pdp.style.removeProperty('--wss-right-top');
        return;
      }
      var gap = 24;
      var offset = stickyHeaderHeight() + gap;
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      /* A column taller than the window gets a negative top: it scrolls until
         its bottom edge is in view, then stays put while the other column ends. */
      var leftTop = Math.min(offset, viewport - left.offsetHeight - gap);
      var rightTop = Math.min(offset, viewport - right.offsetHeight - gap);
      pdp.style.setProperty('--wss-left-top', leftTop + 'px');
      pdp.style.setProperty('--wss-right-top', rightTop + 'px');
    }

    function schedule() {
      if (frame === null) frame = window.requestAnimationFrame(update);
    }

    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', schedule);
    if (window.ResizeObserver) {
      var observer = new ResizeObserver(schedule);
      observer.observe(left);
      observer.observe(right);
    }
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Phone gallery dots                                                  */
  /* ------------------------------------------------------------------ */

  function initGalleryDots(pdp) {
    var gallery = pdp.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var viewer = gallery.querySelector('[data-gallery-viewer]');
    if (!viewer) return;
    var figures = toArray(viewer.querySelectorAll('[data-gallery-figure]'));
    /* Up to 7 dots fit at 44px each on a phone; with more, keep thumbnails */
    if (figures.length < 2 || figures.length > 7) return;

    var dots = null;

    function selectedIndex() {
      var selected = viewer.querySelector('[data-gallery-figure][data-gallery-selected="true"]');
      return selected ? parseInt(selected.getAttribute('data-gallery-index'), 10) || 0 : 0;
    }

    function paint() {
      if (!dots) return;
      var current = selectedIndex();
      toArray(dots.children).forEach(function (dot, index) {
        if (index === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function build() {
      if (dots) return;
      dots = document.createElement('div');
      dots.className = 'wss-dots';
      figures.forEach(function (figure, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'wss-dots__dot';
        dot.setAttribute('aria-label', 'Show image ' + (index + 1) + ' of ' + figures.length);
        dot.addEventListener('click', function () {
          var thumb = gallery.querySelector('[data-gallery-thumbnail][data-gallery-index="' + index + '"]');
          if (thumb) thumb.click();
        });
        dots.appendChild(dot);
      });
      viewer.parentNode.insertBefore(dots, viewer.nextSibling);
      paint();
    }

    function sync() {
      var swiping = viewer.classList.contains('flickity-enabled');
      if (swiping) build();
      gallery.classList.toggle('wss-has-dots', swiping);
      paint();
    }

    new MutationObserver(sync).observe(viewer, {
      attributes: true,
      attributeFilter: ['class', 'data-gallery-selected'],
      subtree: true
    });
    sync();
  }

  /* ------------------------------------------------------------------ */
  /* Phone sticky buy bar                                                */
  /* ------------------------------------------------------------------ */

  function readProductData(section) {
    var script = section && section.querySelector('[data-section-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent).product || null;
    } catch (error) {
      return null;
    }
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function initBuyBar(pdp) {
    var section = pdp.closest('.shopify-section') || document;
    var bar = section.querySelector('[data-wss-buybar]');
    if (!bar || !window.IntersectionObserver) return;
    var form = pdp.querySelector('[data-product-form-regular] form[data-product-form]') || pdp.querySelector('form[data-product-form]');
    var mainButton = form && form.querySelector('[data-product-atc]');
    if (!mainButton) return;

    var product = readProductData(section);
    var priceOut = bar.querySelector('[data-wss-buybar-price]');
    var metaOut = bar.querySelector('[data-wss-buybar-meta]');
    var thumbOut = bar.querySelector('[data-wss-buybar-thumb]');
    var button = bar.querySelector('[data-wss-buybar-atc]');
    var pricing = pdp.querySelector('[data-product-pricing]');
    var root = document.documentElement;

    /* Fixed elements inside transformed ancestors misbehave: live at body level.
       In the theme editor a re-rendered section brings a new bar; drop the old one. */
    toArray(document.querySelectorAll('body > [data-wss-buybar]')).forEach(function (old) {
      if (old !== bar && old.parentNode) old.parentNode.removeChild(old);
    });
    document.body.appendChild(bar);
    bar.hidden = false;
    bar.setAttribute('aria-hidden', 'true');
    bar.inert = true;

    function currentVariant() {
      var idInput = form.querySelector('[name="id"]');
      var id = idInput ? String(idInput.value) : '';
      if (!product || !product.variants) return null;
      for (var k = 0; k < product.variants.length; k += 1) {
        if (String(product.variants[k].id) === id) return product.variants[k];
      }
      return null;
    }

    function sync() {
      var priceEl = pricing && (pricing.querySelector('.price__current [data-price]') || pricing.querySelector('.price__current .money'));
      if (priceEl && priceOut) priceOut.textContent = norm(priceEl.textContent);

      var variant = currentVariant();
      var parts = [];
      if (variant && product && !(product.variants.length === 1 && /^default title$/i.test(variant.title))) {
        parts.push(variant.title);
      }
      var qtyInput = form.querySelector('[data-quantity-input], [name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (qty > 1) parts.push('Qty ' + qty);
      if (metaOut) {
        metaOut.textContent = parts.join(', ');
        metaOut.hidden = !parts.length;
      }

      if (thumbOut && variant && variant.featured_image && variant.featured_image.src) {
        var next = sizedImage(variant.featured_image.src, 120);
        if (thumbOut.getAttribute('src') !== next) thumbOut.setAttribute('src', next);
      }

      var label = mainButton.querySelector('.atc-button--text');
      if (label && button) button.textContent = norm(label.textContent) || button.textContent;
      var disabled = mainButton.disabled || mainButton.classList.contains('disabled');
      button.disabled = disabled;
      button.classList.toggle('is-processing', mainButton.classList.contains('processing'));
    }

    button.addEventListener('click', function () {
      if (mainButton.disabled) return;
      mainButton.click(); /* the theme's own add to cart handles everything */
    });

    var variantSelection = form.querySelector('variant-selection');
    if (variantSelection) variantSelection.addEventListener('variant-change', function () { window.setTimeout(sync, 0); });
    form.addEventListener('change', function () { window.setTimeout(sync, 0); });
    form.addEventListener('input', function () { window.setTimeout(sync, 0); });
    form.addEventListener('click', function () { window.setTimeout(sync, 50); });
    var observer = new MutationObserver(sync);
    observer.observe(mainButton, { attributes: true, childList: true, characterData: true, subtree: true });
    if (pricing) observer.observe(pricing, { childList: true, characterData: true, subtree: true });
    sync();

    var pastButton = false;

    function show(visible) {
      var on = visible && phoneQuery.matches;
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.inert = !on;
      root.classList.toggle('wss-buybar-on', on);
      if (on) root.style.setProperty('--wss-buybar-h', bar.offsetHeight + 'px');
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* Only once the button has scrolled up and out of view */
        pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        show(pastButton);
      });
    }).observe(mainButton);

    if (phoneQuery.addEventListener) phoneQuery.addEventListener('change', function () { show(pastButton); });
    window.addEventListener('resize', function () {
      if (bar.classList.contains('is-visible')) root.style.setProperty('--wss-buybar-h', bar.offsetHeight + 'px');
    });

    /* Tidio chat: lift its bubble above the bar, but not while the chat is open */
    function bindChat() {
      var api = window.tidioChatApi;
      if (!api || !api.on) return;
      api.on('open', function () { root.classList.add('wss-chat-open'); });
      api.on('close', function () { root.classList.remove('wss-chat-open'); });
    }
    if (window.tidioChatApi) bindChat();
    else document.addEventListener('tidioChat-ready', bindChat);
  }

  /* ------------------------------------------------------------------ */
  /* Support video                                                       */
  /* ------------------------------------------------------------------ */

  /*
   * The Support Video tab holds <div class="lazyframe" data-src="...">. The new box is a fresh
   * element (not the old one renamed) so any click handler lazyframe already bound goes with it.
   * Nothing loads until the tab opens: the thumbnail is a lazy image inside the closed <details>.
   * YouTube answers a missing video's thumbnail with a 120px wide placeholder (or an error), which
   * is how a deleted or private video is caught before anyone clicks.
   */
  var VIDEO_TIMEOUT = 15000;
  var PLAY_ICON = '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true" focusable="false"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg>';

  function youtubeId(url) {
    var match = /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|watch\?(?:.*&)?v=)|youtu\.be\/)([\w-]{11})/.exec(url || '');
    return match ? match[1] : null;
  }

  function initVideo(old) {
    var src = (old.getAttribute('data-src') || '').trim();
    if (!src) return;
    var id = youtubeId(src);
    var watchUrl = id ? 'https://www.youtube.com/watch?v=' + id : src;
    var embedUrl = id
      ? 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0'
      : src + (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';

    var box = document.createElement('div');
    box.className = 'wss-video';
    box.innerHTML =
      '<span class="wss-video__spinner" role="status" aria-label="Loading video"></span>' +
      '<button type="button" class="wss-video__play" aria-label="Play support video">' +
      '<span class="wss-video__play-icon">' + PLAY_ICON + '</span></button>' +
      '<p class="wss-video__message" hidden></p>';
    var message = box.querySelector('.wss-video__message');
    var playButton = box.querySelector('.wss-video__play');
    var waitTimer = null;
    var started = false;

    function setState(state) {
      box.classList.remove('is-loading', 'is-ready', 'is-playing', 'is-missing');
      box.classList.add(state);
    }

    function showMessage(text, linkText) {
      message.textContent = text;
      if (linkText) {
        var link = document.createElement('a');
        link.href = watchUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = linkText;
        message.appendChild(document.createTextNode(' '));
        message.appendChild(link);
      }
      message.hidden = false;
    }

    function missing() {
      window.clearTimeout(waitTimer);
      if (started) return;
      setState('is-missing');
      showMessage('This video is not available right now.');
    }

    function ready() {
      window.clearTimeout(waitTimer);
      if (!started && box.classList.contains('is-loading')) setState('is-ready');
    }

    if (id) {
      setState('is-loading');
      var poster = document.createElement('img');
      poster.className = 'wss-video__poster';
      poster.alt = '';
      poster.loading = 'lazy';
      poster.addEventListener('load', function () {
        if (poster.naturalWidth && poster.naturalWidth <= 120) missing();
        else ready();
      });
      poster.addEventListener('error', missing);
      poster.src = 'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg';
      box.insertBefore(poster, box.firstChild);
      /* A thumbnail that never arrives (slow network): offer the play button anyway once the tab is open */
      var details = old.closest('details');
      var startWait = function () {
        if (details && !details.open) return;
        window.clearTimeout(waitTimer);
        waitTimer = window.setTimeout(ready, VIDEO_TIMEOUT);
      };
      if (details) details.addEventListener('toggle', startWait);
      startWait();
    } else {
      setState('is-ready');
    }

    playButton.addEventListener('click', function () {
      started = true;
      window.clearTimeout(waitTimer);
      setState('is-loading');
      var frame = document.createElement('iframe');
      frame.title = 'Support video';
      frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      frame.setAttribute('allowfullscreen', '');
      var slow = window.setTimeout(function () {
        showMessage('The video is taking a while to load.', 'Watch it on YouTube');
      }, VIDEO_TIMEOUT);
      frame.addEventListener('load', function () {
        window.clearTimeout(slow);
        message.hidden = true;
        setState('is-playing');
      });
      frame.src = embedUrl;
      box.appendChild(frame);
    });

    old.parentNode.replaceChild(box, old);
  }

  function initVideos(pdp) {
    toArray(pdp.querySelectorAll('.lazyframe[data-src]')).forEach(initVideo);
  }

  /* ------------------------------------------------------------------ */
  /* Quick description: jump to Product details (phones and tablets)     */
  /* ------------------------------------------------------------------ */

  /* Plain anchor without JavaScript; with it, a smooth scroll that clears a sticky header */
  function initJump(pdp) {
    toArray(pdp.querySelectorAll('[data-wss-jump]')).forEach(function (link) {
      link.addEventListener('click', function (event) {
        var target = document.getElementById((link.getAttribute('href') || '').slice(1));
        if (!target) return;
        event.preventDefault();
        var reduce = media('(prefers-reduced-motion: reduce)').matches;
        var top = target.getBoundingClientRect().top + window.pageYOffset - stickyHeaderHeight() - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function watchVariants(pdp) {
    /* A variant without a SKU hides the SKU row: recheck rows on variant change */
    var selection = pdp.querySelector('variant-selection');
    if (!selection) return;
    selection.addEventListener('variant-change', function () {
      window.setTimeout(function () { markEmptyBlocks(pdp); }, 0);
    });
  }

  function boot() {
    var pdp = document.querySelector('[data-wss-pdp]');
    if (!pdp || pdp.hasAttribute('data-wss-ready')) return;
    pdp.setAttribute('data-wss-ready', '');
    [initDescription, markEmptyBlocks, watchVariants, initStickyColumns, initGalleryDots, initBuyBar, initVideos, initJump].forEach(function (step) {
      try {
        step(pdp);
      } catch (error) {
        if (window.console) console.warn('WSS product layout step failed', error);
      }
    });
  }

  /* Theme editor: a section re-render replaces the markup, so run again */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-wss-pdp]')) boot();
  });

  /* Exposed for testing the description split outside Shopify */
  window.WSSProduct = { buildSections: buildSections, initVideos: initVideos, initJump: initJump };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
